import React, { useRef } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { Avatar, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { globalStore } from '@/common';
import { contexts } from '@/domains';
import { displayUserAvatar, displayUserName } from '@/utils/userDisplay';

type Props = {
  index: number;
  userId: string;
  moveListItem: (dragIndex: number, hoverIndex: number) => void;
  onDrop: (dragIndex: number, hoverIndex: number) => void;
  onRemove: () => void;
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
}

const { DeliveryContext } = contexts;

function ParticipantCard(props: Props): JSX.Element {
  const { userRecord } = DeliveryContext.useContainer();
  const { index, userId, moveListItem, onDrop, onRemove, isDragging } = props;
  const userKey = String(userId);
  const user =
    userRecord[userKey] ?? (globalStore.userMap.get(userKey) as typeof userRecord[string] | undefined);
  const displayName = displayUserName(user);
  const avatarSrc = displayUserAvatar(user);
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDraggingItem }, dragRef] = useDrag({
    type: 'item',
    item: { index },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult<{ hoverIndex: number }>();
      if (dropResult && dropResult.hoverIndex !== undefined) {
        onDrop(item.index, dropResult.hoverIndex);
      }
    },
    collect: (monitor) => ({
      isDraggingItem: monitor.isDragging(),
    }),
  });

  const [, dropRef] = useDrop({
    accept: 'item',
    hover: (item: { index: number }, monitor: DropTargetMonitor) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveListItem(dragIndex, hoverIndex);
      
      item.index = hoverIndex; 
    },
    drop: () => ({ hoverIndex: index }),
  });

  dragRef(dropRef(ref));

  return (
    <div ref={ref} className="w-full flex flex-row justify-between items-center member-card" style={{ cursor: 'grab', marginBottom: 0, borderRadius: 5, opacity: isDraggingItem ? 0.2 : 1 }}>
      <div className="flex items-center">
        <Avatar style={{ marginRight: 20 }} src={avatarSrc}>
          {!avatarSrc && displayName ? displayName.slice(0, 1) : null}
        </Avatar>
        {displayName || userKey}
      </div>
      <div className="flex items-center">
        <Button type="text" className='remove-icon' icon={<CloseOutlined />} onClick={onRemove} />
      </div>
    </div>
  );
}

export default observer(ParticipantCard);
